import React from 'react';
import {
  Typography,
  Grid,
  FormControlLabel,
  Box,
  TextField,
  InputAdornment,
  Switch,
  Paper,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isHeaderOverlayActive } from '../utils/headerTemplates';

interface Header7ExclusiveSettingsProps {
  config: any;
  onHeaderSettingChange: (setting: any, value: any) => void;
}

/**
 * Header 7'ye özel ayarlar: overlay (şeffaf hero üzeri) ve CTA butonu.
 * Sipariş butonu alanlarını (orderButton*) paylaşır; yalnızca bu şablonda "alışveriş" olarak gösterilir.
 */
const Header7ExclusiveSettings: React.FC<Header7ExclusiveSettingsProps> = ({
  config,
  onHeaderSettingChange,
}) => {
  const { t } = useTranslation('salesSystem');
  const overlayOn = isHeaderOverlayActive(config.headerSettings);
  const ctaOn = config.headerSettings.showOrderButton === true;

  const setOverlay = (next: boolean) => {
    onHeaderSettingChange('backgroundColor', next ? 'transparent' : '#ffffff');
    if (next) {
      onHeaderSettingChange('enableHeaderBlur', true);
    }
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.header7.overlayTitle')}
          </Typography>
          <FormControlLabel
            sx={{ m: 0 }}
            control={<Switch checked={overlayOn} onChange={(e) => setOverlay(e.target.checked)} />}
            label={t('ecommerceHeaderSettings.common.active')}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {t('ecommerceHeaderSettings.header7.overlayHelp')}
        </Typography>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
            {t('ecommerceHeaderSettings.header7.ctaTitle')}
          </Typography>
          <FormControlLabel
            sx={{ m: 0 }}
            control={
              <Switch
                checked={ctaOn}
                onChange={(e) => onHeaderSettingChange('showOrderButton', e.target.checked)}
              />
            }
            label={t('ecommerceHeaderSettings.common.active')}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          {t('ecommerceHeaderSettings.header7.ctaHelp')}
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.header7.ctaText')}
              value={config.headerSettings.orderButtonText || 'ALIŞVERİŞ'}
              onChange={(e) => onHeaderSettingChange('orderButtonText', e.target.value)}
              disabled={!ctaOn}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.header7.ctaUrl')}
              value={config.headerSettings.orderButtonUrl || '/cart'}
              onChange={(e) => onHeaderSettingChange('orderButtonUrl', e.target.value)}
              disabled={!ctaOn}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.header7.ctaBackground')}
              value={config.headerSettings.orderButtonBackgroundColor || '#111111'}
              onChange={(e) => onHeaderSettingChange('orderButtonBackgroundColor', e.target.value)}
              disabled={!ctaOn}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={config.headerSettings.orderButtonBackgroundColor || '#111111'}
                      onChange={(e: any) => onHeaderSettingChange('orderButtonBackgroundColor', e.target.value)}
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                      disabled={!ctaOn}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label={t('ecommerceHeaderSettings.header7.ctaTextColor')}
              value={config.headerSettings.orderButtonTextColor || '#ffffff'}
              onChange={(e) => onHeaderSettingChange('orderButtonTextColor', e.target.value)}
              disabled={!ctaOn}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box
                      component="input"
                      type="color"
                      value={config.headerSettings.orderButtonTextColor || '#ffffff'}
                      onChange={(e: any) => onHeaderSettingChange('orderButtonTextColor', e.target.value)}
                      sx={{ border: 'none', width: 24, height: 24, p: 0, background: 'none', cursor: 'pointer' }}
                      disabled={!ctaOn}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Header7ExclusiveSettings;
